"use client"

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type WheelEvent,
} from "react"
import Image from "next/image"
import {
  CHAT_MODEL_SELECTIONS,
  ChatMessage,
  ChatMessageToolCall,
  ChatModelSelection,
  ChatThread,
  ChatUICohort,
  ChatUIDashboardWidget,
  ChatUIMessage,
  ChatUIPromptSnippet,
  ChatUIStorageFile,
  ExternalMCPServer,
  PromptSnippet,
} from "@/schema"
import { PROMPT_SNIPPET_CHAT_TYPES, type PromptSnippetChatType } from "@/schema/prompt-snippet"
import { useChat } from "@ai-sdk/react"
import { useQueryClient, UseQueryResult } from "@tanstack/react-query"
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { type FileUIPart, type SourceUrlUIPart } from "ai"
import { format } from "date-fns"
import {
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Copy,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Square,
  X,
} from "lucide-react"
import { toast } from "sonner"
import { defaultUrlTransform, type UrlTransform } from "streamdown"
import { useStickToBottomContext } from "use-stick-to-bottom"

import { isAllBooksId } from "@/lib/all-books"
import { resolveApiUrl } from "@/hooks/api/base"
import { useAuthorizeExternalMCPOAuth, useFetchExternalMCPServers } from "@/hooks/api/external-mcp-servers"
import { useFetchFeatureFlags } from "@/hooks/api/feature-flags/queries"
import {
  useCreatePromptSnippetOverride,
  useFetchAllPromptSnippets,
  useUpdatePromptSnippet,
} from "@/hooks/api/prompt-snippets"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { useScrollAreaHeight } from "@/hooks/use-scroll-area-height"
import { useScrollNavigation } from "@/hooks/use-scroll-navigation"
import { Attachment, AttachmentInfo, AttachmentPreview, Attachments } from "@/components/ai-elements/attachments"
import { Conversation, ConversationContent, ConversationScrollButton } from "@/components/ai-elements/conversation"
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message"
import {
  PromptInput,
  PromptInputAttachmentButton,
  PromptInputFooter,
  PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputAttachments,
} from "@/components/ai-elements/prompt-input"
import { ActionLinkPart } from "@/components/chat-threads/action-link-part"
import {
  ChatAttachmentTiles,
  ChatPendingAttachments,
  DashboardWidgetAttachment,
  type ChatAttachment,
  type PendingStorageAttachment,
} from "@/components/chat-threads/chat-attachments"
import ChatGreeting from "@/components/chat-threads/chat-greeting"
import { ChatLoadingIndicator } from "@/components/chat-threads/chat-loading-indicator"
import ChatThreadHistory from "@/components/chat-threads/chat-thread-history"
import { ChatToolActivityDisplay, collectChatToolActivities } from "@/components/chat-threads/chat-tool-activity"
import CohortDialog from "@/components/chat-threads/cohort-dialog"
import {
  ExternalMCPPromptEditor,
  type ExternalMCPPromptEditorHandle,
} from "@/components/chat-threads/external-mcp-prompt-editor"
import { MagicButtonsSection } from "@/components/chat-threads/magic-buttons-section"
import { ProductChatGate } from "@/components/chat-threads/product-chat-gate"
import { PromptSnippetPill } from "@/components/chat-threads/prompt-snippet-pill"
import { PromptSnippetSlashMenu } from "@/components/chat-threads/prompt-snippet-slash-menu"
import { ChatRequestNotAcceptedError, TwoStepChatTransport } from "@/components/chat-threads/two-step-chat-transport"
import { EditorRefHandle } from "@/components/lexical"
import CitationBadgeComponent from "@/components/lexical/nodes/citation/component"
import type { Citation } from "@/components/lexical/nodes/citation/node"
import ExternalMCPMentionComponent from "@/components/lexical/nodes/external-mcp-mention/component"
import ObjectLinkComponent from "@/components/lexical/nodes/object-link/component"
import { PromptSnippetDialogForm } from "@/components/settings/prompt-snippets/prompt-snippet-dialog-form"
import { VoiceMicButton } from "@/components/voice-transcription/voice-mic-button"

const CHAT_MODEL_OPTIONS: {
  value: ChatModelSelection
  label: string
  iconSrc: string
}[] = [
  { value: "gpt_latest", label: "GPT-6 Sol", iconSrc: "/openai_icon.svg" },
  { value: "gpt_low", label: "GPT-6 Luna", iconSrc: "/openai_icon.svg" },
  { value: "claude_opus_latest", label: "Claude Opus 5.5", iconSrc: "/claude_icon.svg" },
  { value: "claude_sonnet_latest", label: "Claude Sonnet 5.5", iconSrc: "/claude_icon.svg" },
]

// Matches the backend FileStorable::MAX_FILE_SIZE (20.megabytes).
const MAX_ATTACHMENT_FILE_SIZE = 20 * 1024 * 1024
const MAX_QUEUED_PROMPTS = 1

type ExternalMCPTextSegment = { text: string } | { server: ExternalMCPServer }

const externalMCPTextSegments = (text: string, servers: ExternalMCPServer[]): ExternalMCPTextSegment[] => {
  if (!text || servers.length === 0) return [{ text }]

  const serversBySlug = new Map(servers.map((server) => [server.slug.toLowerCase(), server]))
  const segments: ExternalMCPTextSegment[] = []
  const mentionPattern = /@([a-zA-Z0-9_-]+)/g
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = mentionPattern.exec(text)) !== null) {
    const previousCharacter = text[match.index - 1]
    const nextCharacter = text[match.index + match[0].length]
    if (
      (previousCharacter && /[a-zA-Z0-9_-]/.test(previousCharacter)) ||
      (nextCharacter && /[a-zA-Z0-9_-]/.test(nextCharacter))
    ) {
      continue
    }

    const server = serversBySlug.get(match[1].toLowerCase())
    if (!server) continue

    if (match.index > cursor) segments.push({ text: text.slice(cursor, match.index) })
    segments.push({ server })
    cursor = match.index + match[0].length
  }

  if (cursor < text.length) segments.push({ text: text.slice(cursor) })
  return segments.length > 0 ? segments : [{ text }]
}

const CHAT_SEND_ERROR_FALLBACK = "Something went wrong sending your message. Please try again."

// The chat transport throws `new Error(<Rails response body>)`, so on a 422 the
// message is the raw `{ errors: [...] }` / `{ error: "..." }` JSON. Surface it to the user.
const chatSendErrorMessage = (error: unknown): string => {
  if (!(error instanceof Error) || !error.message) return CHAT_SEND_ERROR_FALLBACK

  try {
    const parsed = JSON.parse(error.message) as { errors?: unknown; error?: unknown }
    if (Array.isArray(parsed.errors) && parsed.errors.length > 0) return parsed.errors.join(". ")
    if (typeof parsed.error === "string" && parsed.error) return parsed.error
  } catch {
    // Not a JSON error body — fall back to the raw message below.
  }

  return error.message || CHAT_SEND_ERROR_FALLBACK
}

const serializableFileParts = (files: PromptInputMessage["files"] | undefined): FileUIPart[] =>
  files?.map((filePart) => {
    const { file, ...part } = filePart
    void file
    return part
  }) ?? []

const uploadFileFromPromptPart = async (filePart: PromptInputMessage["files"][number]): Promise<File> => {
  if (filePart.file) {
    return filePart.file
  }

  if (filePart.url?.startsWith("blob:") || filePart.url?.startsWith("data:")) {
    const response = await fetch(filePart.url)
    const blob = await response.blob()

    return new File([blob], filePart.filename || "attachment", {
      type: filePart.mediaType || blob.type || "application/octet-stream",
    })
  }

  throw new Error(`Missing upload object for ${filePart.filename || "attachment"}`)
}

const rememberOptimisticAttachmentFile = (
  filesByKey: Map<string, Blob>,
  filePart: FileUIPart,
  file: File | undefined
) => {
  if (!file) return
  if (filePart.url) filesByKey.set(filePart.url, file)
  if (filePart.filename) filesByKey.set(filePart.filename, file)
}

const appendChatResponseFormValue = (formData: FormData, key: string, value: unknown) => {
  if (value == null) return

  if (Array.isArray(value)) {
    for (const item of value) {
      appendChatResponseFormValue(formData, `${key}[]`, item)
    }
    return
  }

  if (value instanceof Blob) {
    formData.append(key, value)
    return
  }

  if (typeof value === "object") {
    formData.append(key, JSON.stringify(value))
    return
  }

  formData.append(key, String(value))
}

const buildChatResponseFormData = (body: BodyInit | null | undefined, uploadedFiles: File[]) => {
  const payload = typeof body === "string" ? (JSON.parse(body) as Record<string, unknown>) : {}
  const formData = new FormData()

  for (const [key, value] of Object.entries(payload)) {
    appendChatResponseFormValue(formData, key, value)
  }

  for (const file of uploadedFiles) {
    formData.append("uploaded_files[]", file)
  }

  return formData
}

const STORAGE_FILE_DRAG_MIME_TYPE = "application/x-storage-file"

const DEFAULT_CHAT_MODEL_SELECTION: ChatModelSelection = "gpt_latest"
const CHAT_MODEL_SELECTION_STORAGE_KEY = "chat-model-selection"
const CHAT_MODEL_SELECTION_MIGRATION_KEY = "chat-model-selection-migrated-20260423-latest-keys"
const CHAT_MODEL_SELECTION_SET = new Set<ChatModelSelection>(CHAT_MODEL_SELECTIONS)
const GOOGLE_CHAT_MODEL_SELECTION_SET = new Set([
  "gemini_3_flash",
  "gemini_3_pro",
  "gemini_flash_latest",
  "gemini_pro_latest",
])
const MESSAGE_TIMESTAMP_INTERVAL_MS = 20 * 60 * 1000
const COMPOSER_PADDING_GUTTER = 32

const isChatModelSelection = (value: string): value is ChatModelSelection =>
  CHAT_MODEL_SELECTION_SET.has(value as ChatModelSelection)

const normalizeModelSelection = (value?: string | null): ChatModelSelection | null => {
  if (value === "gpt_advanced") return DEFAULT_CHAT_MODEL_SELECTION
  if (value && GOOGLE_CHAT_MODEL_SELECTION_SET.has(value)) return DEFAULT_CHAT_MODEL_SELECTION
  if (value && isChatModelSelection(value)) return value

  return null
}

const getStoredModelSelection = (): ChatModelSelection | null => {
  if (typeof window === "undefined") return null

  const storedValue = window.localStorage.getItem(CHAT_MODEL_SELECTION_STORAGE_KEY)
  if (storedValue === "gpt_advanced") {
    window.localStorage.setItem(CHAT_MODEL_SELECTION_STORAGE_KEY, DEFAULT_CHAT_MODEL_SELECTION)
    return DEFAULT_CHAT_MODEL_SELECTION
  }
  const hasMigratedStoredDefault = window.localStorage.getItem(CHAT_MODEL_SELECTION_MIGRATION_KEY) === "1"

  if (storedValue && GOOGLE_CHAT_MODEL_SELECTION_SET.has(storedValue)) {
    window.localStorage.setItem(CHAT_MODEL_SELECTION_STORAGE_KEY, DEFAULT_CHAT_MODEL_SELECTION)
    return DEFAULT_CHAT_MODEL_SELECTION
  }

  if (!hasMigratedStoredDefault) {
    window.localStorage.setItem(CHAT_MODEL_SELECTION_MIGRATION_KEY, "1")

    if (storedValue === "gpt_5_2" || storedValue === "gpt_5_4") {
      window.localStorage.setItem(CHAT_MODEL_SELECTION_STORAGE_KEY, "gpt_latest")
      return "gpt_latest"
    }

    if (storedValue === "claude_sonnet_4_5" || storedValue === "claude_sonnet_4_6") {
      window.localStorage.setItem(CHAT_MODEL_SELECTION_STORAGE_KEY, "claude_sonnet_latest")
      return "claude_sonnet_latest"
    }

    if (storedValue === "claude_opus_4_6" || storedValue === "claude_opus_4_7") {
      window.localStorage.setItem(CHAT_MODEL_SELECTION_STORAGE_KEY, "claude_opus_latest")
      return "claude_opus_latest"
    }
  }

  return normalizeModelSelection(window.localStorage.getItem(CHAT_MODEL_SELECTION_STORAGE_KEY))
}

const getInitialModelSelection = (selectedModelSelection?: ChatModelSelection | null): ChatModelSelection =>
  normalizeModelSelection(selectedModelSelection) ?? getStoredModelSelection() ?? DEFAULT_CHAT_MODEL_SELECTION

const promptSnippetToData = (snippet: PromptSnippet): ChatUIPromptSnippet => ({
  prompt_snippet_id: snippet.id,
  prompt_snippet_key: snippet.key,
  system_key: snippet.system_key,
  title: snippet.title,
  content: snippet.content,
  icon: snippet.icon,
  color: snippet.color,
  chat_type: snippet.chat_type,
})

const toPromptSnippetChatType = (chatThreadableType?: string): PromptSnippetChatType | undefined => {
  if (!chatThreadableType) return undefined
  if (chatThreadableType === "Household" || chatThreadableType === "PastClient") return "Client"
  if (["Client", "Prospect", "Meeting", "Book"].includes(chatThreadableType)) {
    return chatThreadableType as PromptSnippetChatType
  }
  return undefined
}

const rawTextFromMessage = (message?: ChatUIMessage): string =>
  message?.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("") ?? ""

const displayTextFromMessage = (message?: ChatUIMessage): string => cleanDisplayText(rawTextFromMessage(message))

const composerTextFromUserMessage = (message?: ChatUIMessage): string =>
  message?.parts
    .map((part) => {
      if (part.type === "text") return part.text
      if (part.type === "data-prompt-snippet") return part.data.content ?? ""

      return ""
    })
    .join("") ?? ""

const ACTION_LINKS_REGEX = /<action_links>[\s\S]*?<\/action_links>/gi
// Strips a block whose closing tag hasn't streamed in yet so raw XML never flashes mid-stream
const UNCLOSED_ACTION_LINKS_REGEX = /<action_links>[\s\S]*$/i
const CITATIONS_REGEX = /<citations>([\s\S]*?)<\/citations>/gi
const CITE_TAG_REGEX = /<cite\b([^>]*)\/?>/gi
const CITE_ATTRIBUTE_REGEX = /(\w+)="([^"]*)"/g
const DISCLAIMER_REGEX = /<disclaimer>[\s\S]*?<\/disclaimer>/gi
const OBJECT_LINK_REGEX = /<object_link\s+objectType="([^"]*)"\s+objectId="([^"]*)">([\s\S]*?)<\/object_link>/gi
const SLANT_OBJECT_MARKDOWN_REGEX = /\]\(slant-object:\/\/([^/\s)]+)\/([^)]+)\)/gi
const MENTION_REGEX = /<(client|prospect|user) id='([^']+)'>([^<]+)<\/\1>/gi

const decodeHtmlAttribute = (value: string): string =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")

const encodeCitationPayload = (citations: Citation[]): string =>
  encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(citations)))))

const decodeCitationPayload = (value: string): Citation[] | null => {
  try {
    return JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(value))))) as Citation[]
  } catch {
    return null
  }
}

const getCitationDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace("www.", "")
  } catch {
    return url
  }
}

const citationLabel = (citations: Citation[]): string => {
  const firstCitation = citations[0]
  if (!firstCitation) return "Source"

  return `${getCitationDomain(firstCitation.url)}${citations.length > 1 ? ` +${citations.length - 1}` : ""}`
}

const escapeMarkdownLinkText = (value: string): string => value.replace(/([\\[\]])/g, "\\$1")

const citationsToMarkdownLink = (citations: Citation[]): string => {
  if (citations.length === 0) return ""

  return `[${escapeMarkdownLinkText(citationLabel(citations))}](#slant-citation/${encodeCitationPayload(citations)})`
}

const parseCitationBlock = (content: string): Citation[] => {
  const citations: Citation[] = []
  let tagMatch: RegExpExecArray | null

  CITE_TAG_REGEX.lastIndex = 0
  while ((tagMatch = CITE_TAG_REGEX.exec(content)) !== null) {
    const attributes: Record<string, string> = {}
    let attributeMatch: RegExpExecArray | null

    CITE_ATTRIBUTE_REGEX.lastIndex = 0
    while ((attributeMatch = CITE_ATTRIBUTE_REGEX.exec(tagMatch[1])) !== null) {
      attributes[attributeMatch[1]] = decodeHtmlAttribute(attributeMatch[2])
    }

    if (attributes.url) {
      citations.push({
        url: attributes.url,
        title: attributes.title || attributes.url,
        snippet: attributes.snippet || "",
      })
    }
  }

  return citations
}

const sourceUrlPartToCitation = (source: SourceUrlUIPart): Citation => ({
  url: source.url,
  title: source.title ?? source.url,
  snippet:
    typeof (source as SourceUrlUIPart & { snippet?: unknown }).snippet === "string"
      ? (source as SourceUrlUIPart & { snippet: string }).snippet
      : "",
})

const formatDisplayText = (
  text: string,
  { renderCitations, fallbackSources = [] }: { renderCitations: boolean; fallbackSources?: SourceUrlUIPart[] }
): string => {
  const formatted = text
    .replace(ACTION_LINKS_REGEX, "")
    .replace(UNCLOSED_ACTION_LINKS_REGEX, "")
    .replace(CITATIONS_REGEX, (_match, citationsContent: string) =>
      renderCitations ? citationsToMarkdownLink(parseCitationBlock(citationsContent)) : ""
    )
    .replace(DISCLAIMER_REGEX, "")
    .replace(OBJECT_LINK_REGEX, (_match, objectType: string, objectId: string, label: string) => {
      const cleanLabel = label.replace(/<[^>]*>/g, "").trim()
      return `[${cleanLabel}](#slant-object/${encodeURIComponent(objectType)}/${encodeURIComponent(objectId)})`
    })
    .replace(SLANT_OBJECT_MARKDOWN_REGEX, (_match, objectType: string, objectId: string) => {
      return `](#slant-object/${encodeURIComponent(safeDecodeURIComponent(objectType))}/${encodeURIComponent(safeDecodeURIComponent(objectId))})`
    })
    .replace(MENTION_REGEX, (_match, _recordType: string, _recordId: string, label: string) => label)
    .trim()

  if (!renderCitations || fallbackSources.length === 0) return formatted

  const fallbackCitationLink = citationsToMarkdownLink(fallbackSources.map(sourceUrlPartToCitation))
  if (!fallbackCitationLink) return formatted

  return `${formatted}${formatted ? " " : ""}${fallbackCitationLink}`
}

const cleanDisplayText = (text: string): string => formatDisplayText(text, { renderCitations: false })

const renderDisplayText = (text: string, fallbackSources?: SourceUrlUIPart[]): string =>
  formatDisplayText(text, { renderCitations: true, fallbackSources })

const hasCitationBlock = (text: string): boolean => {
  CITATIONS_REGEX.lastIndex = 0
  return CITATIONS_REGEX.test(text)
}

const legacyMessageToUIMessage = (message: ChatMessage): ChatUIMessage => ({
  id: message.id,
  role: message.role,
  metadata: {
    created_at: String(message.created_at),
    completed_at: message.updated_at ? String(message.updated_at) : undefined,
  },
  parts: message.content ? [{ type: "text", text: message.content }] : [],
})

const uiMessagesFromThread = (thread?: ChatThread): ChatUIMessage[] =>
  thread?.ui_messages?.length ? thread.ui_messages : (thread?.chat_messages ?? []).map(legacyMessageToUIMessage)

const threadMessagesSyncKey = (thread?: ChatThread): string | undefined => {
  if (!thread) return undefined

  const messageIds = uiMessagesFromThread(thread)
    .map((message) => message.id)
    .join(",")
  return `${thread.id}:${thread.updated_at}:${messageIds}`
}

const streamCancellationApiUrl = (threadId: string) =>
  resolveApiUrl(`chat_threads/${threadId}/chat_stream_cancellation`).toString()

interface ChatViewProps {
  threadId: string
  chatThread: UseQueryResult<ChatThread>
  allThreads?: ChatThread[]
  onThreadSelect?: (threadId: string) => void
  onCreateNewThread?: () => void
  isCreatingThread?: boolean
  lockThreadWhileResponding?: boolean
  threadControlsTitle?: string
  hideGreeting?: boolean
  placeholder?: string
  pendingMessage?: string | null
  pendingMessageId?: string
  onPendingMessageConsumed?: (id: string) => void
  beforeSend?: () => Promise<void>
  onMessageSent?: () => void
  onResponseFinished?: () => void
  onToolCallFinished?: (toolCall: ChatMessageToolCall) => void
  editorRef?: React.MutableRefObject<EditorRefHandle | null>
  initialUserPrompt?: string
  composerFocusKey?: string
  composerActions?: ReactNode
  dashboardWidget?: ChatUIDashboardWidget
  onRemoveDashboardWidget?: (id: string) => void
  onRestoreDashboardWidget?: (widget: ChatUIDashboardWidget) => void
  onRespondingChange?: (responding: boolean) => void
  noticeMessage?: ReactNode
  isBookAssistant?: boolean
  magicButtonContext?: {
    recordType: "client" | "prospect" | "contact" | "business" | "meeting"
    recordId: string
  }
  snippetChatType?: PromptSnippetChatType
  showSnippetBadges?: boolean
  hiddenPromptSnippetKeys?: string[]
  messageListClassName?: string
  userMessageClassName?: string
  fixedModelSelection?: ChatModelSelection
  starterAssistantMessage?: string
  allowMessageQueue?: boolean
  requestContext?: Record<string, unknown>
  assistantAction?: ReactNode
  supportsExternalMCP?: boolean
}

interface QueuedPrompt {
  id: string
  threadId: string
  text: string
  files: PromptInputMessage["files"]
  storageAttachments: PendingStorageAttachment[]
  requestContext?: Record<string, unknown>
}

function revokeQueuedPromptFiles(prompt: QueuedPrompt) {
  for (const file of prompt.files) {
    if (file.url?.startsWith("blob:")) URL.revokeObjectURL(file.url)
  }
}

function textFromReactNode(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(textFromReactNode).join("")
  return ""
}

function parseSlantObjectHref(href?: string) {
  if (!href?.startsWith("#slant-object/")) return null

  const [, objectType, objectId] = href.split("/")
  if (!objectType || !objectId) return null

  return {
    objectType: safeDecodeURIComponent(objectType),
    objectId: safeDecodeURIComponent(objectId),
  }
}

function parseSlantCitationHref(href?: string): Citation[] | null {
  if (!href?.startsWith("#slant-citation/")) return null

  const encoded = href.slice("#slant-citation/".length)
  return decodeCitationPayload(encoded)
}

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function formatMessageTimestamp(value?: string) {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return format(date, "MMMM d, yyyy 'at' h:mm a")
}

function messageTimestampMs(message?: ChatUIMessage) {
  const value = message?.metadata?.created_at
  if (!value) return null

  const timestamp = new Date(value).getTime()
  return Number.isNaN(timestamp) ? null : timestamp
}

function shouldShowMessageTimestamp(message: ChatUIMessage, previousMessage?: ChatUIMessage) {
  const timestamp = messageTimestampMs(message)
  if (timestamp === null) return false
  if (!previousMessage) return true

  const previousTimestamp = messageTimestampMs(previousMessage)
  if (previousTimestamp === null) return false

  return timestamp - previousTimestamp >= MESSAGE_TIMESTAMP_INTERVAL_MS
}

function hasAssistantRenderableContent(message?: ChatUIMessage) {
  if (!message || message.role !== "assistant") return false

  return message.parts.some((part) => {
    if (part.type === "text") return part.text.trim().length > 0
    if (part.type === "source-url") return false

    return true
  })
}

function shouldShowAssistantThinking(message?: ChatUIMessage) {
  if (!message || message.role !== "assistant") return true

  const hasRunningToolActivity = collectChatToolActivities(message).activities.some((activity) => !activity.completed)

  return hasRunningToolActivity || !hasAssistantRenderableContent(message)
}

function MarkdownLink({ href, children, ...props }: ComponentProps<"a">) {
  const citations = parseSlantCitationHref(href)
  if (citations) {
    return <CitationBadgeComponent citations={citations} />
  }

  const objectLink = parseSlantObjectHref(href)
  if (objectLink) {
    return (
      <ObjectLinkComponent
        objectType={objectLink.objectType}
        objectId={objectLink.objectId}
        label={textFromReactNode(children) || objectLink.objectId}
      />
    )
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-blue-accent underline underline-offset-2 hover:opacity-80"
      {...props}
    >
      {children}
    </a>
  )
}

const markdownComponents = {
  a: MarkdownLink,
}

const markdownUrlTransform: UrlTransform = (url, key, node) => {
  if (url.startsWith("#slant-object/")) return url
  if (url.startsWith("#slant-citation/")) return url

  return defaultUrlTransform(url, key, node)
}

export function ChatView(props: Parameters<typeof ChatViewContent>[0]) {
  return (
    <ProductChatGate>
      <ChatViewContent {...props} />
    </ProductChatGate>
  )
}

function ChatViewContent({
  threadId,
  chatThread,
  allThreads,
  onThreadSelect,
  onCreateNewThread,
  isCreatingThread = false,
  lockThreadWhileResponding = false,
  threadControlsTitle,
  hideGreeting = false,
  placeholder,
  pendingMessage,
  pendingMessageId,
  onPendingMessageConsumed,
  beforeSend,
  onMessageSent,
  onResponseFinished,
  onToolCallFinished,
  editorRef: _editorRef,
  initialUserPrompt,
  composerFocusKey,
  composerActions,
  dashboardWidget,
  onRemoveDashboardWidget,
  onRestoreDashboardWidget,
  onRespondingChange,
  noticeMessage,
  isBookAssistant = false,
  magicButtonContext,
  snippetChatType,
  showSnippetBadges = true,
  hiddenPromptSnippetKeys,
  messageListClassName,
  userMessageClassName,
  fixedModelSelection,
  starterAssistantMessage,
  allowMessageQueue = false,
  requestContext,
  assistantAction,
  supportsExternalMCP = true,
}: ChatViewProps) {
  void _editorRef

  const queryClient = useQueryClient()
  const urlBookId = useBookId()
  const getContext = useApiContext()
  const { data: featureFlags } = useFetchFeatureFlags()
  const externalMCPEnabled = supportsExternalMCP && (featureFlags?.includes("external_mcp_servers") ?? false)
  const { data: currentUser } = useFetchCurrentUser({ enabled: externalMCPEnabled })
  const authorizeExternalMCP = useAuthorizeExternalMCPOAuth()
  const { data: fetchedExternalMCPServers = [] } = useFetchExternalMCPServers({
    enabled: externalMCPEnabled,
    chatThreadId: threadId,
  })
  const externalMCPServers = useMemo(
    () => (externalMCPEnabled ? fetchedExternalMCPServers : []),
    [externalMCPEnabled, fetchedExternalMCPServers]
  )
  const { tableTop, containerRef } = useScrollAreaHeight()
  const { data: thread, isLoading: isLoadingThread } = chatThread
  const [selectedModelSelection, setModelSelectionState] = useState<ChatModelSelection>(() =>
    getInitialModelSelection(thread?.model_selection)
  )
  const modelSelection = fixedModelSelection ?? selectedModelSelection
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const appliedFocusKeyRef = useRef<string | undefined>(undefined)
  const submittedDashboardWidgetRef = useRef<ChatUIDashboardWidget | undefined>(undefined)
  const externalMCPPromptEditorRef = useRef<ExternalMCPPromptEditorHandle | null>(null)
  const composerRef = useRef<HTMLDivElement | null>(null)
  const [composerHeight, setComposerHeight] = useState(0)
  const submittedPendingMessageRef = useRef<string | null>(null)
  const automaticMessageRef = useRef<{ text: string; widget?: ChatUIDashboardWidget } | null>(null)
  const submittedInitialPromptRef = useRef(false)
  const syncedThreadMessagesKeyRef = useRef<string | undefined>(undefined)
  const resumedStreamIdRef = useRef<string | undefined>(undefined)
  const pendingUploadFilesRef = useRef<File[]>([])
  const optimisticAttachmentFilesRef = useRef(new Map<string, Blob>())
  const [stopRequested, setStopRequested] = useState(false)
  const [isCancellingStream, setIsCancellingStream] = useState(false)
  const [hasAvailableMagicButtons, setHasAvailableMagicButtons] = useState<boolean | null>(null)
  const [queuedPrompts, setQueuedPrompts] = useState<QueuedPrompt[]>([])
  const [queuedPromptDraft, setQueuedPromptDraft] = useState<QueuedPrompt | null>(null)
  const [queuePaused, setQueuePaused] = useState(false)
  const [dispatchingQueuedPromptId, setDispatchingQueuedPromptId] = useState<string | null>(null)
  const queuedPromptsRef = useRef<QueuedPrompt[]>([])
  const dispatchingQueuedPromptRef = useRef<{ prompt: QueuedPrompt; messageCount: number } | null>(null)
  const removeFailedQueuedPromptMessagesRef = useRef<(messageCount: number) => void>(() => undefined)

  useEffect(() => {
    const url = new URL(window.location.href)
    const status = url.searchParams.get("mcp_oauth")
    if (!status) return

    if (status === "connected") {
      toast.success("MCP server reconnected")
      queryClient.invalidateQueries({ queryKey: ["external-mcp-servers"] })
    } else {
      toast.error("MCP OAuth connection failed. Try reconnecting again.")
    }
    url.searchParams.delete("mcp_oauth")
    window.history.replaceState({}, "", url)
  }, [queryClient])

  const reconnectExternalMCPServer = useCallback(
    (server: ExternalMCPServer) => {
      if (!currentUser?.capabilities.includes("manage_api_access") || !server.requires_reauthorization) return

      authorizeExternalMCP.mutate(
        { id: server.id, returnUri: window.location.href },
        { onSuccess: ({ authorization_url }) => window.location.assign(authorization_url) }
      )
    },
    [authorizeExternalMCP, currentUser?.capabilities.includes("manage_api_access")]
  )

  const restoreDispatchingQueuedPrompt = useCallback(() => {
    const dispatch = dispatchingQueuedPromptRef.current
    if (!dispatch) return

    dispatchingQueuedPromptRef.current = null
    removeFailedQueuedPromptMessagesRef.current(dispatch.messageCount)
    setQueuedPrompts((current) => {
      if (current.some((prompt) => prompt.id === dispatch.prompt.id)) return current
      if (current.length >= MAX_QUEUED_PROMPTS) return current

      return [dispatch.prompt, ...current]
    })
    setQueuePaused(true)
  }, [])

  const initialMessages = useMemo(() => uiMessagesFromThread(thread), [thread])
  const threadSyncKey = useMemo(() => threadMessagesSyncKey(thread), [thread])

  useEffect(() => {
    if (!thread) return

    setModelSelectionState(
      initialMessages.length > 0 ? getInitialModelSelection(thread.model_selection) : getInitialModelSelection()
    )
  }, [initialMessages.length, thread])

  useEffect(() => () => optimisticAttachmentFilesRef.current.clear(), [threadId])

  // The composer is absolutely positioned, so it can't push messages up; reserve its
  // height as bottom padding or attachments will hide the latest message on short viewports.
  useLayoutEffect(() => {
    const composer = composerRef.current
    if (!composer) return

    const updateHeight = () => setComposerHeight(composer.offsetHeight)
    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(composer)
    return () => observer.disconnect()
  }, [])

  const handleModelSelectionChange = useCallback((nextModelSelection: ChatModelSelection) => {
    setModelSelectionState(nextModelSelection)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CHAT_MODEL_SELECTION_STORAGE_KEY, nextModelSelection)
    }
  }, [])

  const transport = useMemo(
    () =>
      new TwoStepChatTransport<ChatUIMessage>({
        threadId,
        getContext,
        buildBody: ({ messages, body, trigger, messageId }) => {
          const requestBody = body as { user_prompt?: string } | undefined
          const targetMessage =
            trigger === "regenerate-message"
              ? previousUserMessage(messages as ChatUIMessage[], messageId)
              : messageId
                ? ((messages as ChatUIMessage[]).find((message) => message.id === messageId) ??
                  (messages as ChatUIMessage[]).at(-1))
                : (messages as ChatUIMessage[]).at(-1)

          return {
            ...body,
            trigger,
            message_id: messageId,
            message: targetMessage,
            user_prompt: requestBody?.user_prompt ?? rawTextFromMessage(targetMessage),
          }
        },
        buildFetchInit: (init, context) => {
          const headers = new Headers(init?.headers)
          if (context.token) {
            headers.set("Authorization", `Bearer ${context.token}`)
          }

          const uploadedFiles = pendingUploadFilesRef.current
          if (uploadedFiles.length > 0) {
            headers.delete("Content-Type")
            return {
              ...init,
              body: buildChatResponseFormData(init?.body, uploadedFiles),
              headers,
            }
          }

          return { ...init, headers }
        },
      }),
    [getContext, threadId]
  )

  const restoreAutomaticMessage = useCallback(() => {
    const submitted = automaticMessageRef.current
    if (!submitted) return
    automaticMessageRef.current = null
    if (submitted.widget) onRestoreDashboardWidget?.(submitted.widget)
    if (externalMCPEnabled && externalMCPPromptEditorRef.current) {
      if (!externalMCPPromptEditorRef.current.getValue()) externalMCPPromptEditorRef.current.setValue(submitted.text)
    } else if (textareaRef.current && !textareaRef.current.value) {
      textareaRef.current.value = submitted.text
    }
  }, [externalMCPEnabled, onRestoreDashboardWidget])

  const { messages, sendMessage, setMessages, status, stop, regenerate, resumeStream } = useChat<ChatUIMessage>({
    id: threadId,
    messages: initialMessages,
    transport,
    experimental_throttle: 50,
    onError: (error) => {
      toast.error(chatSendErrorMessage(error))
      restoreAutomaticMessage()
      if (error instanceof ChatRequestNotAcceptedError) restoreDispatchingQueuedPrompt()
      if (allowMessageQueue && queuedPrompts.length > 0) setQueuePaused(true)
    },
    onData: (part) => {
      if (part.type === "data-tool-call" && part.data.finished_message) {
        onToolCallFinished?.(part.data)
      }
    },
    onFinish: ({ finishReason, isAbort, isDisconnect, isError }) => {
      if (isAbort || isDisconnect || isError || finishReason === "error") restoreAutomaticMessage()
      else automaticMessageRef.current = null
      const submittedWidget = submittedDashboardWidgetRef.current
      submittedDashboardWidgetRef.current = undefined
      if (submittedWidget && !isAbort && !isDisconnect && !isError && finishReason !== "error") {
        onRemoveDashboardWidget?.(submittedWidget.id)
      }
      if (
        allowMessageQueue &&
        queuedPrompts.length > 0 &&
        (isAbort || isDisconnect || isError || finishReason === "error")
      ) {
        setQueuePaused(true)
      }
      queryClient.invalidateQueries({ queryKey: ["chat_thread", threadId] })
      queryClient.invalidateQueries({ queryKey: ["chat_threads_list"] })
      onResponseFinished?.()
    },
  })
  removeFailedQueuedPromptMessagesRef.current = (messageCount) => {
    setMessages((current) => current.slice(0, messageCount))
  }

  useEffect(() => {
    const activeStreamId = thread?.active_stream_id
    if (!activeStreamId) {
      resumedStreamIdRef.current = undefined
      return
    }
    if ((status !== "ready" && status !== "error") || resumedStreamIdRef.current === activeStreamId) return

    resumedStreamIdRef.current = activeStreamId
    void resumeStream()
  }, [resumeStream, status, thread?.active_stream_id])

  useEffect(() => {
    if (status !== "ready" || !thread || !threadSyncKey) return
    if (syncedThreadMessagesKeyRef.current === threadSyncKey) return

    syncedThreadMessagesKeyRef.current = threadSyncKey
    setMessages(uiMessagesFromThread(thread))
  }, [setMessages, status, thread, threadSyncKey])

  useEffect(() => {
    if (status === "ready" || status === "error") {
      setStopRequested(false)
    }
  }, [status])

  const currentChatType = snippetChatType || toPromptSnippetChatType(thread?.chat_threadable_type)
  const shouldShowSnippetBadges = showSnippetBadges && thread?.chat_threadable_type !== "PastClient"
  const { data: promptSnippetsData } = useFetchAllPromptSnippets({
    enabled: shouldShowSnippetBadges && !!currentChatType && (!isAllBooksId(urlBookId) || !!thread),
    chatType: currentChatType,
    includeSystem: true,
    bookId: thread?.book_id,
  })

  const submitText = useCallback(
    async (
      text: string,
      options?: {
        files?: PromptInputMessage["files"]
        storageAttachments?: PendingStorageAttachment[]
        automationId?: string
        promptSnippet?: PromptSnippet
        requestContext?: Record<string, unknown>
      }
    ) => {
      const trimmedText = text.trim()
      const storageAttachmentsOption = options?.storageAttachments ?? []
      if (!trimmedText && !options?.files?.length && storageAttachmentsOption.length === 0) return
      await beforeSend?.()
      setStopRequested(false)
      const submittedAt = new Date().toISOString()

      const fileParts = serializableFileParts(options?.files)
      pendingUploadFilesRef.current =
        options?.files?.length && fileParts.length ? await Promise.all(options.files.map(uploadFileFromPromptPart)) : []
      fileParts.forEach((filePart, index) => {
        rememberOptimisticAttachmentFile(
          optimisticAttachmentFilesRef.current,
          filePart,
          pendingUploadFilesRef.current[index]
        )
      })

      const storageFileParts = storageAttachmentsOption.map((attachment) => ({
        type: "data-storage-file" as const,
        id: `${attachment.bookId}:${attachment.id}`,
        data: {
          file_id: attachment.id,
          book_id: attachment.bookId,
          file_name: attachment.name,
          provider: attachment.provider,
        },
      }))

      const body = {
        ...(options?.requestContext ?? requestContext),
        automation_id: options?.automationId,
        model_selection: modelSelection,
        prompt_snippet_id: options?.promptSnippet?.id || undefined,
        prompt_snippet_key: options?.promptSnippet?.key,
        user_prompt: options?.promptSnippet?.content,
        storage_file_ids: storageAttachmentsOption.length
          ? storageAttachmentsOption.map((attachment) => ({
              file_id: attachment.id,
              book_id: attachment.bookId,
            }))
          : undefined,
      }

      const widgetParts = dashboardWidget
        ? [{ type: "data-dashboard-widget" as const, id: dashboardWidget.id, data: dashboardWidget }]
        : []
      submittedDashboardWidgetRef.current = dashboardWidget

      try {
        if (options?.promptSnippet) {
          const snippet = promptSnippetToData(options.promptSnippet)
          await sendMessage(
            {
              metadata: { created_at: submittedAt },
              role: "user",
              parts: [
                {
                  type: "data-prompt-snippet",
                  id: snippet.prompt_snippet_id || snippet.prompt_snippet_key || snippet.system_key || snippet.title,
                  data: snippet,
                },
              ],
            },
            {
              body,
            }
          )
        } else {
          await sendMessage(
            fileParts.length || storageFileParts.length || widgetParts.length
              ? {
                  metadata: { created_at: submittedAt },
                  role: "user",
                  parts: [
                    ...(trimmedText ? [{ type: "text" as const, text: trimmedText }] : []),
                    ...fileParts,
                    ...storageFileParts,
                    ...widgetParts,
                  ],
                }
              : { text: trimmedText, metadata: { created_at: submittedAt } },
            {
              body,
            }
          )
        }

        onMessageSent?.()
      } finally {
        pendingUploadFilesRef.current = []
      }
    },
    [beforeSend, dashboardWidget, modelSelection, onMessageSent, requestContext, sendMessage]
  )

  const submitMagicButton = useCallback(
    async (automationId: string, title: string) => {
      await submitText(`Run the "${title}" magic button automation`, {
        automationId,
      })
    },
    [submitText]
  )

  useEffect(() => {
    if (
      !pendingMessage ||
      isLoadingThread ||
      (status !== "ready" && status !== "error") ||
      thread?.active_stream_id ||
      isCancellingStream
    )
      return
    const messageId = pendingMessageId ?? pendingMessage
    if (submittedPendingMessageRef.current === messageId) return
    submittedPendingMessageRef.current = messageId
    if (pendingMessageId) {
      automaticMessageRef.current = { text: pendingMessage, widget: dashboardWidget }
      onPendingMessageConsumed?.(pendingMessageId)
    }
    void submitText(pendingMessage).catch((error) => {
      restoreAutomaticMessage()
      toast.error(chatSendErrorMessage(error))
    })
  }, [
    dashboardWidget,
    isCancellingStream,
    isLoadingThread,
    onPendingMessageConsumed,
    pendingMessage,
    pendingMessageId,
    restoreAutomaticMessage,
    status,
    submitText,
    thread?.active_stream_id,
  ])

  useEffect(() => {
    if (!initialUserPrompt || isLoadingThread || messages.length > 0 || submittedInitialPromptRef.current) return
    submittedInitialPromptRef.current = true
    void submitText(initialUserPrompt).catch((error) => toast.error(chatSendErrorMessage(error)))
  }, [initialUserPrompt, isLoadingThread, messages.length, submitText])

  const hasStartedChat = messages.length > 0
  const starterAssistantUIMessage: ChatUIMessage | null = starterAssistantMessage
    ? {
        id: "starter-assistant-message",
        role: "assistant",
        parts: [{ type: "text", text: starterAssistantMessage }],
      }
    : null
  const usesStartedChatLayout = hasStartedChat || !!starterAssistantUIMessage
  const promptSnippets = (promptSnippetsData?.prompt_snippets ?? []).filter(
    (snippet) => !hiddenPromptSnippetKeys?.includes(snippet.system_key || snippet.key)
  )
  const hasMagicButtonSurface = !!magicButtonContext?.recordType && !!magicButtonContext?.recordId
  const showMagicButtons = !usesStartedChatLayout && hasMagicButtonSurface && hasAvailableMagicButtons !== false
  const showPromptSnippets = !usesStartedChatLayout && promptSnippets.length > 0
  const effectiveStatus = stopRequested ? "ready" : status
  const hasActiveStream = !!thread?.active_stream_id
  const isResponding = (status === "submitted" || status === "streaming" || hasActiveStream) && !stopRequested
  useEffect(() => {
    onRespondingChange?.(isResponding)
  }, [isResponding, onRespondingChange])

  useEffect(() => {
    if (!composerFocusKey || appliedFocusKeyRef.current === composerFocusKey || !textareaRef.current) return
    appliedFocusKeyRef.current = composerFocusKey
    textareaRef.current.focus()
  }, [composerFocusKey, isLoadingThread])
  const queueIsFull = allowMessageQueue && queuedPrompts.length >= MAX_QUEUED_PROMPTS
  const showThinkingIndicator = isResponding && shouldShowAssistantThinking(messages.at(-1))
  const householdId =
    thread && (thread.chat_threadable_type === "Client" || thread.chat_threadable_type === "Household")
      ? thread.chat_threadable_id
      : undefined

  const [pendingStorageAttachments, setPendingStorageAttachments] = useState<PendingStorageAttachment[]>([])

  const handlePromptSubmit = useCallback(
    async (message: PromptInputMessage) => {
      if (allowMessageQueue && queuedPrompts.length >= MAX_QUEUED_PROMPTS) return

      const storageAttachments = pendingStorageAttachments
      setPendingStorageAttachments([])
      const text = (externalMCPPromptEditorRef.current?.getValue() ?? message.text)
        .replace(/[\u0000\u200B]/g, "")
        .trim()
      if (!text.trim() && message.files.length === 0 && storageAttachments.length === 0) return
      externalMCPPromptEditorRef.current?.clear()

      if (
        allowMessageQueue &&
        (isLoadingThread ||
          isResponding ||
          hasActiveStream ||
          isCancellingStream ||
          queuedPrompts.length > 0 ||
          dispatchingQueuedPromptId)
      ) {
        if (queuedPrompts.length >= MAX_QUEUED_PROMPTS) return

        setQueuedPrompts((current) => [
          ...current,
          {
            id: crypto.randomUUID(),
            threadId,
            text,
            files: message.files,
            storageAttachments,
            requestContext,
          },
        ])
        return
      }

      await submitText(text, { files: message.files, storageAttachments })
    },
    [
      allowMessageQueue,
      dispatchingQueuedPromptId,
      hasActiveStream,
      isCancellingStream,
      isLoadingThread,
      isResponding,
      pendingStorageAttachments,
      queuedPrompts.length,
      requestContext,
      submitText,
      threadId,
    ]
  )

  const handleAttachmentError = useCallback(
    (error: { code: "max_files" | "max_file_size" | "accept"; message: string }) => {
      toast.error(error.message)
    },
    []
  )

  const removePendingStorageAttachment = useCallback((id: string, bookId: string) => {
    setPendingStorageAttachments((prev) =>
      prev.filter((attachment) => attachment.id !== id || attachment.bookId !== bookId)
    )
  }, [])

  const handleStorageDragOver = useCallback((event: React.DragEvent<HTMLFormElement>) => {
    if (event.dataTransfer.types.includes(STORAGE_FILE_DRAG_MIME_TYPE)) {
      event.preventDefault()
      event.dataTransfer.dropEffect = "copy"
    }
  }, [])

  const handleStorageDrop = useCallback((event: React.DragEvent<HTMLFormElement>) => {
    const data = event.dataTransfer.getData(STORAGE_FILE_DRAG_MIME_TYPE)
    if (!data) return

    event.preventDefault()

    try {
      const parsed = JSON.parse(data) as { id?: string; book_id?: string; name?: string; provider?: string }
      const id = parsed?.id
      const bookId = parsed?.book_id
      if (!id || !bookId) return

      setPendingStorageAttachments((prev) => {
        if (prev.some((attachment) => attachment.id === id && attachment.bookId === bookId)) return prev
        return [
          ...prev,
          {
            id,
            bookId,
            name: parsed.name || "Attachment",
            provider: parsed.provider,
          },
        ]
      })
    } catch (error) {
      console.error("Failed to parse dropped storage file", error)
    }
  }, [])

  const magicButtonContextKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const contextKey = `${magicButtonContext?.recordType ?? ""}:${magicButtonContext?.recordId ?? ""}`
    if (magicButtonContextKeyRef.current === null) {
      magicButtonContextKeyRef.current = contextKey
      return
    }
    if (magicButtonContextKeyRef.current === contextKey) return
    magicButtonContextKeyRef.current = contextKey
    setHasAvailableMagicButtons(null)
  }, [magicButtonContext?.recordId, magicButtonContext?.recordType])

  const handleStop = useCallback(() => {
    const lastUserMessage = previousUserMessage(messages)
    const retryText = composerTextFromUserMessage(lastUserMessage)
    const retryWidget = lastUserMessage?.parts.find((part) => part.type === "data-dashboard-widget")
    if (retryWidget) onRestoreDashboardWidget?.(retryWidget.data)

    setStopRequested(true)
    setIsCancellingStream(true)
    if (allowMessageQueue && queuedPrompts.length > 0) setQueuePaused(true)
    setMessages(removeMessagesAfterLastUserMessage(messages))
    if (externalMCPEnabled && externalMCPPromptEditorRef.current) {
      if (!externalMCPPromptEditorRef.current.getValue()) externalMCPPromptEditorRef.current.setValue(retryText)
      externalMCPPromptEditorRef.current.focus()
    } else if (textareaRef.current) {
      if (!textareaRef.current.value) textareaRef.current.value = retryText
      textareaRef.current.focus()
    }
    stop()
    void getContext()
      .then((context) => {
        const url = resolveApiUrl(`chat_threads/${threadId}/chat_stream_cancellation`)
        if (context.bookId) {
          url.searchParams.set("book_id", context.bookId)
        }

        const headers = new Headers()
        if (context.token) {
          headers.set("Authorization", `Bearer ${context.token}`)
        }

        return fetch(url.toString(), { method: "POST", headers })
      })
      .catch(() => undefined)
      .finally(() => {
        setIsCancellingStream(false)
        queryClient.invalidateQueries({ queryKey: ["chat_thread", threadId] })
      })
  }, [
    allowMessageQueue,
    externalMCPEnabled,
    getContext,
    messages,
    onRestoreDashboardWidget,
    queryClient,
    queuedPrompts.length,
    setMessages,
    stop,
    threadId,
  ])

  useEffect(() => {
    setQueuedPrompts((current) => {
      current.forEach(revokeQueuedPromptFiles)
      return []
    })
    dispatchingQueuedPromptRef.current = null
    setIsCancellingStream(false)
    setQueuePaused(false)
    setDispatchingQueuedPromptId(null)
    externalMCPPromptEditorRef.current?.clear()
  }, [threadId])

  useEffect(() => {
    if (!allowMessageQueue || queuePaused || queuedPrompts.length === 0) return
    if (isLoadingThread) return
    if (status !== "ready" && status !== "error") return
    if (hasActiveStream || isCancellingStream) return
    if (dispatchingQueuedPromptId) return

    const queuedPrompt = queuedPrompts[0]
    if (queuedPrompt.threadId !== threadId) return
    dispatchingQueuedPromptRef.current = { prompt: queuedPrompt, messageCount: messages.length }
    setDispatchingQueuedPromptId(queuedPrompt.id)
    setQueuedPrompts((current) => current.filter((prompt) => prompt.id !== queuedPrompt.id))

    void submitText(queuedPrompt.text, {
      files: queuedPrompt.files,
      requestContext: queuedPrompt.requestContext,
      storageAttachments: queuedPrompt.storageAttachments,
    })
      .catch(restoreDispatchingQueuedPrompt)
      .finally(() => {
        if (dispatchingQueuedPromptRef.current?.prompt.id === queuedPrompt.id) {
          dispatchingQueuedPromptRef.current = null
        }
        setDispatchingQueuedPromptId(null)
      })
  }, [
    allowMessageQueue,
    dispatchingQueuedPromptId,
    hasActiveStream,
    isCancellingStream,
    isLoadingThread,
    messages.length,
    queuePaused,
    queuedPrompts,
    restoreDispatchingQueuedPrompt,
    status,
    submitText,
    threadId,
  ])

  useEffect(() => {
    queuedPromptsRef.current = queuedPrompts
  }, [queuedPrompts])

  useEffect(() => {
    if (queuedPrompts.length === 0 && queuePaused) setQueuePaused(false)
  }, [queuePaused, queuedPrompts.length])

  useEffect(
    () => () => {
      queuedPromptsRef.current.forEach(revokeQueuedPromptFiles)
    },
    []
  )

  const removeQueuedPrompt = useCallback((id: string) => {
    setQueuedPrompts((current) => {
      const removedPrompt = current.find((prompt) => prompt.id === id)
      if (removedPrompt) revokeQueuedPromptFiles(removedPrompt)
      return current.filter((prompt) => prompt.id !== id)
    })
  }, [])

  const editQueuedPrompt = useCallback(
    (id: string) => {
      const prompt = queuedPrompts.find((queuedPrompt) => queuedPrompt.id === id)
      if (!prompt) return

      setQueuedPrompts((current) => current.filter((queuedPrompt) => queuedPrompt.id !== id))
      setQueuedPromptDraft(prompt)
      setPendingStorageAttachments(prompt.storageAttachments)

      if (externalMCPEnabled && externalMCPPromptEditorRef.current) {
        externalMCPPromptEditorRef.current.setValue(prompt.text)
        externalMCPPromptEditorRef.current.focus()
      } else if (textareaRef.current) {
        textareaRef.current.value = prompt.text
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(prompt.text.length, prompt.text.length)
      }
    },
    [externalMCPEnabled, queuedPrompts]
  )

  const handleQueuedPromptDraftRestored = useCallback(() => {
    setQueuedPromptDraft(null)
  }, [])

  const handleRegenerate = useCallback(
    (messageId: string) => {
      setStopRequested(false)
      void (async () => {
        await beforeSend?.()
        await regenerate({ messageId, body: { model_selection: modelSelection, ...requestContext } })
      })().catch((error) => toast.error(chatSendErrorMessage(error)))
    },
    [beforeSend, modelSelection, regenerate, requestContext]
  )

  const threadControlsDisabled =
    isCreatingThread ||
    (lockThreadWhileResponding && (isResponding || hasActiveStream || isCancellingStream || !!pendingMessage))
  const threadControls = (
    <>
      {onCreateNewThread && (
        <Button
          variant="ghost"
          size="iconXs"
          onClick={() => {
            if (!threadControlsDisabled) onCreateNewThread()
          }}
          disabled={threadControlsDisabled}
          aria-label="New chat"
        >
          {isCreatingThread ? <Loader2 className="text-icon animate-spin" /> : <Plus className="text-icon" />}
        </Button>
      )}
      {allThreads && onThreadSelect && (
        <ChatThreadHistory
          threads={allThreads}
          onThreadSelect={(selectedThreadId) => {
            if (!threadControlsDisabled) onThreadSelect(selectedThreadId)
          }}
          currentThreadId={threadId}
          disabled={threadControlsDisabled}
        />
      )}
    </>
  )

  return (
    <div ref={containerRef} className="flex h-full flex-col" style={{ height: `calc(100vh - ${tableTop}px)` }}>
      <div className="relative flex h-full flex-col">
        {(allThreads || onCreateNewThread) &&
          (threadControlsTitle ? (
            <div className="flex h-11 shrink-0 items-center justify-between gap-2 border-b px-3">
              <p className="text-sm font-semibold whitespace-nowrap">{threadControlsTitle}</p>
              <div className="flex shrink-0 items-center gap-1">{threadControls}</div>
            </div>
          ) : (
            <div className="bg-background absolute top-0 z-30 m-4 flex w-fit items-center justify-end gap-1.5 self-end rounded-xl border px-2 py-1">
              {threadControls}
            </div>
          ))}

        <Conversation className="min-h-0 flex-1">
          <ConversationContent
            className={cn(
              "short:pt-6 mx-auto w-full max-w-[50rem] px-6 pt-10",
              !usesStartedChatLayout && "short:pb-32 pb-44",
              messageListClassName
            )}
            style={usesStartedChatLayout ? { paddingBottom: composerHeight + COMPOSER_PADDING_GUTTER } : undefined}
          >
            {starterAssistantUIMessage && (
              <ChatUIMessageView
                message={starterAssistantUIMessage}
                isLast={false}
                isResponding={false}
                onRegenerate={handleRegenerate}
                assistantAction={assistantAction}
                showTimestamp={false}
              />
            )}
            {hasStartedChat &&
              messages.map((message, index) => {
                if (message.role === "assistant" && !hasAssistantRenderableContent(message)) return null

                return (
                  <ChatUIMessageView
                    key={message.id}
                    externalMCPServers={externalMCPServers}
                    householdId={householdId}
                    isLast={index === messages.length - 1}
                    isResponding={isResponding}
                    message={message}
                    onRegenerate={handleRegenerate}
                    assistantAction={assistantAction}
                    showTimestamp={shouldShowMessageTimestamp(message, messages[index - 1])}
                    optimisticAttachmentFiles={optimisticAttachmentFilesRef.current}
                    userMessageClassName={userMessageClassName}
                  />
                )
              })}
            <ThinkingIndicator visible={showThinkingIndicator} className="-mt-2!" />
          </ConversationContent>
          <KeepConversationBottomOnComposerResize
            composerHeight={composerHeight}
            enabled={allowMessageQueue && isResponding}
          />
          <ConversationScrollButton />
        </Conversation>

        {usesStartedChatLayout && (
          <div className="from-background pointer-events-none absolute inset-x-0 bottom-0 z-10 h-12 bg-gradient-to-t to-transparent" />
        )}

        <div
          ref={composerRef}
          className={cn(
            "absolute right-4 left-4 z-20 mx-auto flex flex-col",
            usesStartedChatLayout && "bottom-4 max-w-[50rem] gap-3",
            !usesStartedChatLayout && showMagicButtons && "short:gap-2 top-0 bottom-4 max-w-7xl gap-4",
            !usesStartedChatLayout && !showMagicButtons && "top-0 bottom-4 max-w-2xl justify-center gap-4",
            !usesStartedChatLayout && !showMagicButtons && !hideGreeting && "short:-translate-y-4 -translate-y-12"
          )}
        >
          {showMagicButtons ? (
            <MagicButtonsSection
              hideGreeting={hideGreeting}
              hasStartedChat={hasStartedChat}
              recordType={magicButtonContext?.recordType}
              recordId={magicButtonContext?.recordId}
              onMagicButtonClick={submitMagicButton}
              onButtonsResolved={setHasAvailableMagicButtons}
              className="min-h-0 flex-1 pr-0 pl-8"
            />
          ) : !usesStartedChatLayout && !hideGreeting ? (
            <div className="min-h-0 w-full">
              <ChatGreeting hasStartedChat={hasStartedChat} className="justify-start px-0 text-left" />
            </div>
          ) : null}

          {showPromptSnippets && (
            <PromptSnippetSuggestions
              snippets={promptSnippets}
              onSelect={(snippet) => submitText(snippet.content, { promptSnippet: snippet })}
              disabled={isResponding}
              className={cn(showMagicButtons && "shorter:hidden px-1")}
            />
          )}

          {allowMessageQueue && queuedPrompts.length > 0 && (
            <QueuedPromptList
              externalMCPServers={externalMCPServers}
              prompts={queuedPrompts}
              paused={queuePaused}
              onEdit={editQueuedPrompt}
              onRemove={removeQueuedPrompt}
              onResume={() => setQueuePaused(false)}
            />
          )}
          <div className="relative w-full">
            {shouldShowSnippetBadges && (
              <PromptSnippetSlashMenu
                snippets={promptSnippets}
                textareaRef={textareaRef}
                disabled={isResponding}
                onSelect={(snippet) => submitText(snippet.content, { promptSnippet: snippet })}
              />
            )}
            <PromptInput
              onSubmit={handlePromptSubmit}
              multiple
              globalDrop
              accept=".pdf,.docx,.txt,.csv,.xlsx,.png,.jpg,.jpeg,.gif"
              maxFileSize={MAX_ATTACHMENT_FILE_SIZE}
              onError={handleAttachmentError}
              onDragOver={handleStorageDragOver}
              onDrop={handleStorageDrop}
              inputGroupClassName={cn(isBookAssistant && "bg-background dark:bg-background", "short:min-h-20")}
            >
              <ChatPendingAttachments
                storageAttachments={pendingStorageAttachments}
                onRemoveStorageAttachment={removePendingStorageAttachment}
                dashboardWidget={dashboardWidget}
                onRemoveDashboardWidget={onRemoveDashboardWidget}
              />
              {queuedPromptDraft && (
                <QueuedPromptDraftRestorer
                  key={queuedPromptDraft.id}
                  prompt={queuedPromptDraft}
                  onRestored={handleQueuedPromptDraftRestored}
                />
              )}
              <div className="flex w-full px-4 pt-3">
                {externalMCPEnabled ? (
                  <ExternalMCPPromptEditor
                    ref={externalMCPPromptEditorRef}
                    servers={externalMCPServers}
                    placeholder={placeholder ?? "Ask anything..."}
                    submitDisabled={queueIsFull || (!allowMessageQueue && isResponding)}
                    canReconnect={currentUser?.capabilities.includes("manage_api_access") ?? false}
                    reconnecting={authorizeExternalMCP.isPending}
                    onReconnect={reconnectExternalMCPServer}
                  />
                ) : (
                  <PromptInputTextarea
                    ref={textareaRef}
                    placeholder={placeholder ?? "Ask anything..."}
                    submitDisabled={queueIsFull || (!allowMessageQueue && isResponding)}
                    className="placeholder:text-muted-foreground short:min-h-12 min-h-16 min-w-48 flex-1 px-0 py-0 text-base md:text-base"
                  />
                )}
              </div>
              <PromptInputFooter className="justify-end gap-2 px-4 pb-3">
                <PromptInputTools className="gap-2">
                  {!fixedModelSelection && (
                    <AIModelSelector
                      value={modelSelection}
                      onChange={handleModelSelectionChange}
                      disabled={isResponding}
                    />
                  )}
                  {composerActions}
                  <PromptInputAttachmentButton disabled={!allowMessageQueue && isResponding} />
                  <VoiceMicButton
                    editorRef={externalMCPEnabled ? externalMCPPromptEditorRef : textareaRef}
                    localActive
                    size="iconXs"
                    className="rounded-full"
                    disabled={!allowMessageQueue && isResponding}
                  />
                </PromptInputTools>
                {allowMessageQueue && isResponding && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    aria-label="Stop updating"
                    onClick={handleStop}
                    className="size-9 rounded-full"
                  >
                    <Square className="size-4" strokeWidth={0} fill="var(--color-neutral-600)" />
                  </Button>
                )}
                <PromptInputSubmit
                  aria-label={
                    queueIsFull ? "Queue full" : allowMessageQueue && isResponding ? "Add to queue" : "Submit"
                  }
                  title={
                    queueIsFull
                      ? "Only one message can be queued at a time. Wait for it to start or remove it."
                      : undefined
                  }
                  status={allowMessageQueue && isResponding ? "ready" : effectiveStatus}
                  onStop={allowMessageQueue ? undefined : handleStop}
                  disabled={queueIsFull}
                  size="icon-sm"
                  className="bg-brand hover:bg-brand-hover size-9 rounded-full text-black disabled:opacity-50"
                >
                  {!allowMessageQueue && effectiveStatus === "submitted" ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : !allowMessageQueue && effectiveStatus === "streaming" ? (
                    <Square className="size-4" strokeWidth={0} fill="var(--color-neutral-600)" />
                  ) : (
                    <ArrowUp className="size-5!" />
                  )}
                </PromptInputSubmit>
              </PromptInputFooter>
            </PromptInput>
          </div>
          {!usesStartedChatLayout && noticeMessage && (
            <div className="border-primary/20 bg-primary/10 text-primary w-full rounded-xl border px-4 py-3 text-sm">
              {noticeMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ExternalMCPMentionPill({ server }: { server: ExternalMCPServer }) {
  const pill = (
    <span>
      <ExternalMCPMentionComponent name={server.name} requiresReauthorization={server.requires_reauthorization} />
    </span>
  )

  if (!server.requires_reauthorization) return pill

  return (
    <Tooltip>
      <TooltipTrigger asChild>{pill}</TooltipTrigger>
      <TooltipContent>Reconnect {server.name} before using it in chat.</TooltipContent>
    </Tooltip>
  )
}

async function copyTextToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    return
  } catch {
    const textArea = document.createElement("textarea")
    textArea.value = text
    textArea.style.position = "fixed"
    textArea.style.left = "-9999px"
    textArea.style.top = "0"
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    document.execCommand("copy")
    document.body.removeChild(textArea)
  }
}

function previousUserMessage(messages: ChatUIMessage[], messageId?: string): ChatUIMessage | undefined {
  const assistantIndex = messageId ? messages.findIndex((message) => message.id === messageId) : messages.length
  const searchEnd = assistantIndex === -1 ? messages.length : assistantIndex
  return messages
    .slice(0, searchEnd)
    .reverse()
    .find((message) => message.role === "user")
}

function removeMessagesAfterLastUserMessage(messages: ChatUIMessage[]): ChatUIMessage[] {
  const lastUserIndex = messages.map((message) => message.role).lastIndexOf("user")
  if (lastUserIndex === -1) return messages

  return messages.slice(0, lastUserIndex)
}

function QueuedPromptList({
  externalMCPServers,
  prompts,
  paused,
  onEdit,
  onRemove,
  onResume,
}: {
  externalMCPServers: ExternalMCPServer[]
  prompts: QueuedPrompt[]
  paused: boolean
  onEdit: (id: string) => void
  onRemove: (id: string) => void
  onResume: () => void
}) {
  return (
    <div className="bg-muted overflow-hidden rounded-xl border shadow-sm">
      <div className="max-h-48 divide-y overflow-y-auto">
        {prompts.map((prompt, index) => {
          const attachmentNames = [
            ...prompt.files.map((file) => file.filename || "Attachment"),
            ...prompt.storageAttachments.map((attachment) => attachment.name),
          ]
          const label = prompt.text.trim() || attachmentNames.join(", ") || "Attachment"

          return (
            <div key={prompt.id} className="flex items-center gap-2 px-3 py-2">
              <div className="line-clamp-2 min-w-0 flex-1 text-sm">
                {prompt.text.trim() ? <UserTextPart text={label} externalMCPServers={externalMCPServers} /> : label}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconXs"
                    aria-label={`Edit queued message ${index + 1}`}
                    onClick={() => onEdit(prompt.id)}
                    className="hover:bg-foreground/15! focus-visible:bg-foreground/15! shrink-0"
                  >
                    <Pencil className="size-3.5!" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit queued message</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconXs"
                    aria-label={`Remove queued message ${index + 1}`}
                    onClick={() => onRemove(prompt.id)}
                    className="hover:bg-foreground/15! focus-visible:bg-foreground/15! shrink-0"
                  >
                    <X className="size-4.5!" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove queued message</TooltipContent>
              </Tooltip>
            </div>
          )
        })}
      </div>
      {paused && (
        <div className="flex items-center justify-end gap-2 border-t px-3 py-1.5">
          <Badge variant="secondary">Paused</Badge>
          <Button type="button" variant="ghost" size="xs" onClick={onResume}>
            Resume
          </Button>
        </div>
      )}
    </div>
  )
}

function QueuedPromptDraftRestorer({ prompt, onRestored }: { prompt: QueuedPrompt; onRestored: () => void }) {
  const attachments = usePromptInputAttachments()

  useEffect(() => {
    let cancelled = false

    const restoreFiles = async () => {
      try {
        const files = await Promise.all(prompt.files.map(uploadFileFromPromptPart))
        if (cancelled) return

        attachments.files.forEach((file) => attachments.remove(file.id))
        attachments.add(files)
        revokeQueuedPromptFiles(prompt)
      } catch {
        if (!cancelled) toast.error("The queued message was restored, but its attachments could not be reloaded.")
      } finally {
        if (!cancelled) onRestored()
      }
    }

    void restoreFiles()

    return () => {
      cancelled = true
    }
  }, [attachments, onRestored, prompt])

  return null
}

function PromptSnippetSuggestions({
  snippets,
  onSelect,
  disabled,
  className,
}: {
  snippets: PromptSnippet[]
  onSelect: (snippet: PromptSnippet) => void
  disabled?: boolean
  className?: string
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { canScrollLeft, canScrollRight, scroll } = useScrollNavigation(scrollRef)

  const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    if (!el) return
    if (el.scrollWidth <= el.clientWidth) return
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
    const atStart = e.deltaY < 0 && el.scrollLeft === 0
    const atEnd = e.deltaY > 0 && el.scrollLeft >= el.scrollWidth - el.clientWidth
    if (atStart || atEnd) return
    el.scrollLeft += e.deltaY
    e.preventDefault()
  }

  return (
    <div className={cn("relative w-full", className)}>
      <div
        onClick={() => scroll("left")}
        className={cn(
          "from-background absolute inset-y-0 left-0 z-10 flex w-12 items-center justify-start bg-gradient-to-r from-30% to-transparent transition-opacity",
          canScrollLeft ? "cursor-pointer opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        <ChevronLeft className="text-foreground size-5" />
      </div>

      <div
        ref={scrollRef}
        onWheel={handleWheel}
        className="flex w-full gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {snippets.map((snippet) => (
          <button
            key={snippet.id ?? snippet.key}
            type="button"
            onClick={() => onSelect(snippet)}
            disabled={disabled}
            className="shrink-0 rounded-full disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PromptSnippetPill
              title={snippet.title}
              icon={snippet.icon}
              color={snippet.color}
              className="h-7 px-2 pr-3 text-base!"
            />
          </button>
        ))}
      </div>

      <div
        onClick={() => scroll("right")}
        className={cn(
          "from-background absolute inset-y-0 right-0 z-10 flex w-12 items-center justify-end bg-gradient-to-l from-30% to-transparent transition-opacity",
          canScrollRight ? "cursor-pointer opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        <ChevronRight className="text-foreground size-5" />
      </div>
    </div>
  )
}

function AIModelSelector({
  value,
  onChange,
  disabled,
}: {
  value: ChatModelSelection
  onChange: (value: ChatModelSelection) => void
  disabled?: boolean
}) {
  const selected = CHAT_MODEL_OPTIONS.find((option) => option.value === value) ?? CHAT_MODEL_OPTIONS[0]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled} className="bg-transparent">
          <Image
            src={selected.iconSrc}
            alt={`${selected.label} icon`}
            width={14}
            height={14}
            className="shrink-0 rounded-xs"
          />
          <span className="truncate text-sm">{selected.label}</span>
          <ChevronsUpDown className="text-muted-foreground size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56 p-1">
        {CHAT_MODEL_OPTIONS.map((option) => {
          const isSelected = option.value === value

          return (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => onChange(option.value)}
              className="data-[highlighted]:bg-tinted flex items-center justify-between gap-3 rounded-md px-2.5 py-2"
            >
              <span className="flex items-center gap-2">
                <Image
                  src={option.iconSrc}
                  alt={`${option.label} icon`}
                  width={16}
                  height={16}
                  className="shrink-0 rounded-xs"
                />
                <span className="text-sm">{option.label}</span>
              </span>
              {isSelected && <Check className="text-icon size-4" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ChatUIMessageView({
  message,
  externalMCPServers = [],
  householdId,
  isLast,
  isResponding,
  onRegenerate,
  assistantAction,
  showTimestamp,
  optimisticAttachmentFiles,
  userMessageClassName,
}: {
  message: ChatUIMessage
  externalMCPServers?: ExternalMCPServer[]
  householdId?: string
  isLast: boolean
  isResponding: boolean
  onRegenerate: (messageId: string) => void
  assistantAction?: ReactNode
  showTimestamp: boolean
  optimisticAttachmentFiles?: Map<string, Blob>
  userMessageClassName?: string
}) {
  const text = displayTextFromMessage(message)
  const hasPromptSnippet = message.parts.some((part) => part.type === "data-prompt-snippet")
  const timestamp = showTimestamp ? formatMessageTimestamp(message.metadata?.created_at) : null
  const userAttachments =
    message.role === "user" ? collectUserAttachments(message.parts, optimisticAttachmentFiles) : []
  const hasOnlyAttachments = message.role === "user" && !text && userAttachments.length > 0 && !hasPromptSnippet

  return (
    <div className="flex w-full flex-col gap-2">
      {timestamp && <div className="text-muted-foreground w-full text-center text-sm">{timestamp}</div>}
      <Message from={message.role} className={message.role === "user" ? userMessageClassName : undefined}>
        <ChatAttachmentTiles attachments={userAttachments} />
        {message.role === "user" &&
          message.parts.map((part) =>
            part.type === "data-dashboard-widget" ? (
              <div key={part.id ?? part.data.id} className="ml-auto max-w-full">
                <DashboardWidgetAttachment widget={part.data} />
              </div>
            ) : null
          )}
        {!hasOnlyAttachments && (
          <MessageContent
            className={cn(
              "text-base!",
              message.role === "user" && hasPromptSnippet
                ? "group-[.is-user]:bg-transparent! group-[.is-user]:px-0! group-[.is-user]:py-0!"
                : undefined
            )}
          >
            <ChatMessageParts
              message={message}
              externalMCPServers={externalMCPServers}
              householdId={householdId}
              isAnimating={isLast && isResponding}
              hasPromptSnippet={hasPromptSnippet}
            />
          </MessageContent>
        )}
        {message.role === "assistant" && text && isLast && (
          <MessageActions className="text-muted-foreground pl-1">
            <MessageAction tooltip="Copy" size="iconXs" onClick={() => copyTextToClipboard(text)}>
              <Copy />
            </MessageAction>
            <MessageAction
              tooltip="Regenerate"
              size="iconXs"
              onClick={() => onRegenerate(message.id)}
              disabled={isResponding}
            >
              <RefreshCw />
            </MessageAction>
            {assistantAction}
          </MessageActions>
        )}
      </Message>
    </div>
  )
}

function UserTextPart({ text, externalMCPServers = [] }: { text: string; externalMCPServers?: ExternalMCPServer[] }) {
  if (!text) return null

  return (
    <div className="whitespace-pre-wrap">
      {externalMCPTextSegments(text.replace(/[\u0000\u200B]/g, "").trim(), externalMCPServers).map((segment, index) =>
        "server" in segment ? (
          <ExternalMCPMentionPill key={`${segment.server.id}-${index}`} server={segment.server} />
        ) : (
          <span key={index}>{segment.text}</span>
        )
      )}
    </div>
  )
}

function AssistantTextPart({
  text,
  isAnimating,
  fallbackSources,
}: {
  text: string
  isAnimating: boolean
  fallbackSources?: SourceUrlUIPart[]
}) {
  return (
    <MessageResponse
      className="text-base!"
      components={markdownComponents}
      isAnimating={isAnimating}
      urlTransform={markdownUrlTransform}
    >
      {renderDisplayText(text, fallbackSources)}
    </MessageResponse>
  )
}

function ThinkingIndicator({ visible, className }: { visible: boolean; className?: string }) {
  return (
    <Message from="assistant" className={cn(className, !visible && "hidden")}>
      <ChatLoadingIndicator className="-ml-1.5" />
    </Message>
  )
}

function KeepConversationBottomOnComposerResize({
  composerHeight,
  enabled,
}: {
  composerHeight: number
  enabled: boolean
}) {
  const { scrollToBottom } = useStickToBottomContext()

  useLayoutEffect(() => {
    if (!enabled) return

    void scrollToBottom({ animation: "instant" })
  }, [composerHeight, enabled, scrollToBottom])

  return null
}

function ChatMessageParts({
  message,
  externalMCPServers,
  householdId,
  isAnimating,
  hasPromptSnippet,
}: {
  message: ChatUIMessage
  externalMCPServers: ExternalMCPServer[]
  householdId?: string
  isAnimating: boolean
  hasPromptSnippet: boolean
}) {
  const sources = message.parts.filter((part): part is SourceUrlUIPart => part.type === "source-url")
  const hasInlineCitations = message.parts.some((part) => part.type === "text" && hasCitationBlock(part.text))
  const lastTextPartIndex = message.parts.reduce(
    (lastIndex, part, index) => (part.type === "text" ? index : lastIndex),
    -1
  )

  const {
    activities: toolActivities,
    activityPartIndexes,
    associatedStatusIndexes,
  } = collectChatToolActivities(message)
  const progressUpdate = message.parts.findLast((part) => part.type === "data-progress-update")
  const progressMessage = progressUpdate?.data.message ?? undefined
  const showProgressUpdate = message.role === "assistant" && progressUpdate?.data.visible !== false && !!progressMessage

  const renderPart = (part: ChatUIMessage["parts"][number], index: number) => {
    switch (part.type) {
      case "text":
        if (message.role === "user" && hasPromptSnippet) return null
        if (message.role === "user") {
          return <UserTextPart key={index} text={part.text} externalMCPServers={externalMCPServers} />
        }
        if (showProgressUpdate) return null
        return (
          <AssistantTextPart
            key={index}
            text={part.text}
            isAnimating={isAnimating}
            fallbackSources={!hasInlineCitations && index === lastTextPartIndex ? sources : undefined}
          />
        )
      case "file":
        if (message.role === "user") return null
        return <FilePart key={index} part={part} />
      case "data-status":
        if (associatedStatusIndexes.has(index)) return null
        return <StatusPart key={part.id ?? index} message={part.data.message} />
      case "data-progress-update":
        return null
      case "data-tool-call":
      case "data-automation-action":
        return null
      case "data-cohort":
        return <CohortPart key={part.id ?? part.data.id ?? index} cohort={part.data} />
      case "data-action-link":
        return (
          <ActionLinkPart
            key={part.id ?? `${part.data.type}-${part.data.id}`}
            link={part.data}
            householdId={householdId}
            disabled={isAnimating}
          />
        )
      case "data-storage-file":
        if (message.role === "user") return null
        return <StorageFilePart key={part.id ?? part.data.file_id ?? index} file={part.data} />
      case "data-prompt-snippet":
        return <PromptSnippetPart key={part.id ?? part.data.prompt_snippet_key ?? index} snippet={part.data} />
      case "data-mention":
        return (
          <Badge key={part.id ?? index} variant="outline">
            {part.data.label}
          </Badge>
        )
      case "data-disclaimer":
        return (
          <p key={part.id ?? index} className="text-muted-foreground text-xs">
            {part.data.text}
          </p>
        )
      case "source-url":
        return null
      default:
        return null
    }
  }

  // Keep activity and status ahead of final content so late-arriving tool finish
  // events do not visually reorder a streaming response.
  const indexedParts = message.parts.map((part, index) => ({ part, index }))
  const renderableParts = indexedParts.filter(({ index }) => !activityPartIndexes.has(index))
  const orderedParts =
    message.role === "assistant"
      ? [
          ...renderableParts.filter(({ part }) => part.type === "data-status"),
          ...renderableParts.filter(({ part }) => part.type !== "data-status"),
        ]
      : renderableParts

  return (
    <>
      {showProgressUpdate && <AssistantTextPart text={progressMessage} isAnimating={false} />}
      <ChatToolActivityDisplay activities={toolActivities} message={message} isAnimating={isAnimating} />
      {orderedParts.map(({ part, index }) => renderPart(part, index))}
      {sources.length > 0 && lastTextPartIndex === -1 && <SourceParts sources={sources} />}
    </>
  )
}

function FilePart({ part }: { part: FileUIPart }) {
  return (
    <Attachments variant="inline">
      <Attachment data={{ ...part, id: part.filename ?? part.url }}>
        <AttachmentPreview />
        <AttachmentInfo />
      </Attachment>
    </Attachments>
  )
}

function optimisticAttachmentFileForPart(part: FileUIPart, filesByKey?: Map<string, Blob>) {
  return (
    (part.url ? filesByKey?.get(part.url) : undefined) ?? (part.filename ? filesByKey?.get(part.filename) : undefined)
  )
}

function collectUserAttachments(
  parts: ChatUIMessage["parts"],
  optimisticAttachmentFiles?: Map<string, Blob>
): ChatAttachment[] {
  const attachments: ChatAttachment[] = []
  parts.forEach((part, index) => {
    if (part.type === "file") {
      attachments.push({
        key: part.url ?? part.filename ?? `file-${index}`,
        url: part.url,
        file: optimisticAttachmentFileForPart(part, optimisticAttachmentFiles),
        filename: part.filename,
        mediaType: part.mediaType,
      })
      return
    }
    if (part.type === "data-storage-file") {
      attachments.push({
        key: part.data.file_id ?? part.data.download_url ?? part.data.file_name ?? `storage-${index}`,
        url: part.data.view_url ?? part.data.download_url,
        filename: part.data.file_name,
        mediaType: part.data.file_type,
        size: part.data.file_size,
      })
    }
  })
  return attachments
}

function SourceParts({ sources }: { sources: SourceUrlUIPart[] }) {
  return <CitationBadgeComponent citations={sources.map(sourceUrlPartToCitation)} />
}

function StatusPart({ message }: { message?: string }) {
  if (!message) return null
  return <div className="text-muted-foreground text-sm">{message}</div>
}

function CohortPart({ cohort }: { cohort: ChatUICohort }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="w-fit">
        View cohort
      </Button>
      <CohortDialog cohortId={cohort.id} open={open} onOpenChange={setOpen} />
    </>
  )
}

function StorageFilePart({ file }: { file: ChatUIStorageFile }) {
  // Skip internal model file references (e.g., OpenAI `cfile_*` IDs) and any
  // attachments without a real download URL — they have nothing to link to.
  if (!file.download_url) return null
  if (file.file_name?.startsWith("cfile_")) return null

  return (
    <a href={file.download_url} target="_blank" rel="noreferrer" className="w-fit">
      <Badge variant="outline" className="w-fit gap-1">
        <Paperclip />
        {file.file_name ?? "Storage file"}
      </Badge>
    </a>
  )
}

function PromptSnippetPart({ snippet }: { snippet: ChatUIPromptSnippet }) {
  const updateSnippet = useUpdatePromptSnippet()
  const createPromptSnippetOverride = useCreatePromptSnippetOverride()
  const [editing, setEditing] = useState(false)
  const closeDialog = () => setEditing(false)
  const bookId = useBookId()

  if (!snippet.title) return null

  const systemKey = snippet.system_key || snippet.prompt_snippet_key
  const isSystemDefault = !snippet.prompt_snippet_id && !!systemKey
  const chatType = (
    PROMPT_SNIPPET_CHAT_TYPES.includes(snippet.chat_type as PromptSnippetChatType) ? snippet.chat_type : "Client"
  ) as PromptSnippetChatType
  const params = new URLSearchParams()
  if (bookId && !isAllBooksId(bookId)) {
    params.set("bookId", bookId)
  }
  const managePromptSnippetsHref = `/settings/prompts/prompt-snippets${params.size ? `?${params.toString()}` : ""}`

  return (
    <>
      <PromptSnippetPill
        title={snippet.title}
        icon={snippet.icon}
        color={snippet.color}
        className="w-full"
        onEditClick={() => setEditing(true)}
        editTooltip="Customize this snippet"
      />
      {editing && (
        <PromptSnippetDialogForm
          open={editing}
          onOpenChange={setEditing}
          dialogTitle="Edit prompt snippet"
          defaultValues={{
            title: snippet.title || "",
            content: snippet.content || "",
            icon: snippet.icon || "wand",
            color: snippet.color || "#0EA5E9",
            chat_type: chatType,
          }}
          onSubmit={async (values) => {
            if (isSystemDefault) {
              await createPromptSnippetOverride.mutateAsync({
                system_key: systemKey,
                ...values,
              })
            } else if (snippet.prompt_snippet_id) {
              await updateSnippet.mutateAsync({
                id: snippet.prompt_snippet_id,
                promptSnippet: values,
              })
            }
            closeDialog()
          }}
          onCancel={closeDialog}
          submitting={isSystemDefault ? createPromptSnippetOverride.isPending : updateSnippet.isPending}
          submitLabel="Save"
          managePromptSnippetsHref={managePromptSnippetsHref}
        />
      )}
    </>
  )
}
